import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Download, FileText, Globe, Mail, ShoppingCart } from "lucide-react";
import {
  researchProductsQueryOptions,
  formatPrice,
  formatPublishDate,
  sortByPublishDateDesc,
  cleanDetail,
} from "@/lib/store.queries";
import type { ResearchProduct } from "@/lib/store.queries";
import { useCart } from "@/lib/cart";
import { publicSettingsQueryOptions } from "@/lib/settings.queries";
import { mailtoLink } from "@/lib/settings.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ContactForm } from "@/components/site/ContactForm";
import { PageHero } from "@/components/site/PageHero";

export const Route = createFileRoute("/research")({
  head: () => ({
    meta: [
      { title: "Strategic Research — Medical Insight" },
      {
        name: "description",
        content:
          "Medical Insight strategic market studies on injectables, energy-based devices, skincare, and the global medical aesthetic market.",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(researchProductsQueryOptions());
  },
  component: ResearchPage,
});

function ResearchPage() {
  return (
    <div className="bg-background">
      <PageHero
        page="research"
        kicker="Medical Insight"
        title="Strategic Research"
      />

      <div className="bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <Suspense
            fallback={<div className="text-sm text-muted-foreground">Loading reports…</div>}
          >
            <ProductGrid />
          </Suspense>

          <LicenseOptions />

          <section className="mt-16">
            <div className="border-b pb-3">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
                Request Custom Research
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Need a study that isn't in the catalog? Tell us what you're trying to answer and we
                will scope it.
              </p>
            </div>
            <div className="mt-6">
              <ContactForm variant="research" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * The address hash as a candidate study handle. Hashes come from outside
 * (newsletters, bookmarks, hand-typed), so a malformed percent-encoding must
 * not throw: the raw text is used as-is and simply matches nothing.
 */
function hashToHandle(hash: string): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function ProductGrid() {
  const { data: rows } = useSuspenseQuery(researchProductsQueryOptions());
  // Strict newest-first by publish date, decided here rather than by the API.
  const products = useMemo(() => sortByPublishDateDesc(rows), [rows]);
  // Deep link: /research#<handle> opens that study. Anything else (no hash,
  // a legacy newsletter anchor such as #GAM, a retired edition's handle)
  // matches no card and the catalogue opens normally.
  const hashHandle = useLocation({ select: (l) => hashToHandle(l.hash) });

  if (products.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No reports are available right now. Please check back shortly.
      </p>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) => (
        <ProductCard
          key={p.id}
          product={p}
          openOnArrival={hashHandle !== "" && p.handle === hashHandle}
        />
      ))}
    </div>
  );
}

/** Strip markup from the Shopify description for the card's teaser line. */
function toExcerpt(html: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;|&rsquo;/g, "'")
    .replace(/&quot;|&rdquo;|&ldquo;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function ProductCard({
  product,
  openOnArrival = false,
}: {
  product: ResearchProduct;
  /** True while the address hash names this study. */
  openOnArrival?: boolean;
}) {
  const { cart, addItem, adding } = useCart();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const excerpt = useMemo(() => toExcerpt(product.description_html), [product.description_html]);
  const inCart = (cart?.lines ?? []).some((l) => l.variantId === product.shopify_variant_id);

  // Runs once per arrival: when this card mounts as the hash target (the
  // products have loaded by then) or when the hash changes to it. Closing the
  // dialog changes neither dependency, so it never reopens; the hash stays in
  // the address so the link can be shared. The router scrolls to the hash too,
  // but it can run before this card exists, so the scroll is repeated here.
  useEffect(() => {
    if (!openOnArrival) return;
    document.getElementById(product.handle)?.scrollIntoView();
    setDetailsOpen(true);
    // The hash moved on to another study (back/forward): close this one.
    return () => setDetailsOpen(false);
  }, [openOnArrival, product.handle]);

  return (
    <div
      id={product.handle}
      className="flex scroll-mt-20 flex-col overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-sm"
    >
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          className="aspect-[3/4] w-full border-b bg-muted object-cover"
        />
      ) : (
        <div className="flex aspect-[3/4] w-full items-center justify-center border-b bg-muted text-muted-foreground">
          <FileText className="h-10 w-10" />
        </div>
      )}

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-semibold leading-snug">{product.title}</h3>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {product.publish_date && (
            <Badge variant="secondary">{formatPublishDate(product.publish_date, "long")}</Badge>
          )}
          {product.pages != null && <Badge variant="outline">{product.pages} pages</Badge>}
        </div>

        <div className="mt-3 text-lg font-semibold">
          {formatPrice(product.price_cents, product.currency)}
        </div>

        {excerpt && (
          <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground text-justify hyphens-auto">
            {excerpt}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                Details
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="pr-6 text-left leading-snug">{product.title}</DialogTitle>
              </DialogHeader>
              <ProductDetails product={product} />
            </DialogContent>
          </Dialog>

          {product.summary_pdf_url && (
            <Button asChild variant="ghost" size="sm">
              <a href={product.summary_pdf_url} target="_blank" rel="noreferrer">
                <Download className="mr-1 h-3.5 w-3.5" /> Download Summary
              </a>
            </Button>
          )}
        </div>

        <Button
          className="mt-3 w-full"
          disabled={inCart || adding}
          onClick={() => addItem(product.shopify_variant_id)}
        >
          <ShoppingCart className="mr-1.5 h-4 w-4" />
          {inCart ? "In cart" : "Add to cart"}
        </Button>
      </div>
    </div>
  );
}

function ProductDetails({ product }: { product: ResearchProduct }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {product.publish_date && (
          <Badge variant="secondary">{formatPublishDate(product.publish_date, "long")}</Badge>
        )}
        {product.pages != null && <Badge variant="outline">{product.pages} pages</Badge>}
        <span className="ml-auto text-lg font-semibold">
          {formatPrice(product.price_cents, product.currency)}
        </span>
      </div>

      {product.description_html && (
        <div
          className="tiptap-content max-w-none"
          dangerouslySetInnerHTML={{ __html: product.description_html }}
        />
      )}

      {(product.details_description || product.details.length > 0) && (
        <div className="rounded-lg border bg-muted/30 p-4">
          {product.details_description && (
            <p className="text-sm leading-relaxed">{product.details_description}</p>
          )}
          {product.details.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {product.details.map((d, i) => (
                <li key={i}>{cleanDetail(d)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {product.summary_pdf_url && (
        <Button asChild variant="outline" size="sm">
          <a href={product.summary_pdf_url} target="_blank" rel="noreferrer">
            <Download className="mr-1 h-3.5 w-3.5" /> Download Summary
          </a>
        </Button>
      )}
    </div>
  );
}

function LicenseOptions() {
  return (
    <section className="mt-16 grid gap-5 sm:grid-cols-2">
      <LicenseCard
        icon={<FileText className="h-5 w-5 text-primary" />}
        title="Annual Research Subscription"
        description="Full-year access to every study published in the subscription period, including updates and analyst briefings."
        cta="Inquire about subscriptions"
        subject="Annual Research Subscription inquiry"
      />
      <LicenseCard
        icon={<Globe className="h-5 w-5 text-primary" />}
        title="Global License"
        description="Enterprise-wide distribution rights across all offices and affiliates, with internal reuse of charts and data."
        cta="Inquire about a global license"
        subject="Global License inquiry"
      />
    </section>
  );
}

function LicenseCard({
  icon,
  title,
  description,
  cta,
  subject,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  subject: string;
}) {
  // Settings > Organization > "Research inquiries email"; defaults to the address the page shipped with.
  const { data: settings } = useQuery(publicSettingsQueryOptions());
  return (
    <div className="flex flex-col rounded-lg border bg-card p-6">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <div className="mt-4">
        <Button asChild variant="outline" size="sm">
          <a href={mailtoLink(settings!.org.researchEmail, subject)}>
            <Mail className="mr-1.5 h-3.5 w-3.5" /> {cta}
          </a>
        </Button>
      </div>
    </div>
  );
}
